import { Table, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FaEye, FaEdit, FaClock } from "react-icons/fa";
import {
    HourglassSplit,
    SendFill,
    Search,
    ArrowClockwise,
    CheckCircleFill,
    XCircleFill,
    ExclamationCircle,
} from "react-bootstrap-icons";
import "./ResultTable.css";

const normalize = (text: string) =>
    text
        ? text.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim()
        : "";

const getSituationConfig = (situacaoRaw: string) => {
    const situacao = normalize(situacaoRaw);

    if (situacao.includes("documentacao pendente")) {
        return { icon: <HourglassSplit />, className: "status-pendente-sindicato" };
    }
    if (situacao.includes("documentacao enviada")) {
        return { icon: <SendFill />, className: "status-enviado" };
    }
    if (situacao.includes("adequacao pendente")) {
        return { icon: <HourglassSplit />, className: "status-adequacao-recusa" };
    }
    if (situacao.includes("analise pendente")) {
        return { icon: <Search />, className: "status-pendente-analista" };
    }
    if (situacao.includes("analise em andamento")) {
        return { icon: <ArrowClockwise />, className: "status-andamento" };
    }
    if (situacao.includes("analise concluida")) {
        return { icon: <CheckCircleFill color="#067647" />, className: "status-plain" };
    }
    if (situacao.includes("inapto")) {
        return { icon: <XCircleFill color="#BA0C2F" />, className: "status-plain" };
    }

    return { icon: <ExclamationCircle />, className: "status-default" };
};

interface ShippingRow {
    id: number;
    solicitacao: string;
    situacao: string;
    envioDocumentacao: string | null;
    ultimaAtualizacao: string | null;
    solicitacaoAdequacao: string | null;
    adequacaoDocumentacao: string | null;
    conclusaoAnalise: string | null;
    ano: number;
}

function linhaTemDataEnvioDocumentacao(
    envioDocumentacao: string | null | undefined,
): boolean {
    return (
        envioDocumentacao != null && String(envioDocumentacao).trim() !== ""
    );
}


function getLabelAcaoPrincipalPorLinha(item: ShippingRow): "Editar" | "Visualizar" {
    const s = normalize(item.situacao);
    const temEnvio = linhaTemDataEnvioDocumentacao(item.envioDocumentacao);

    if (s.includes("adequacao pendente")) return "Editar";
    if (s.includes("documentacao pendente") && !temEnvio) return "Editar";

    if (s.includes("documentacao enviada")) return "Visualizar";
    if (s.includes("analise concluida")) return "Visualizar";
    if (s.includes("analise pendente")) return "Visualizar";
    if (s.includes("analise em andamento")) return "Visualizar";
    if (s.includes("documentacao pendente") && temEnvio) return "Visualizar";
    if (temEnvio) return "Visualizar";

    return "Editar";
}

interface ResultTableProps {
    currentItems: ShippingRow[];
    handleSort: (key: string) => void;
    getSortIcon: (key: string) => string;
    onView: (item: ShippingRow) => void;
    onOpenHistory: (item: ShippingRow) => void;
    historyLoading?: boolean;
    formatDate: (date: string | null | undefined) => string;
}

const renderSortableTitle = (
    titulo: string,
    key: string,
    getSortIcon: (key: string) => string,
) => {
    const partes = String(titulo).trim().split(/\s+/);
    const empilharDuasLinhas = partes.length === 2;

    return (
        <span
            className={`analysis-sort-title${
                empilharDuasLinhas ? " analysis-sort-title--stacked" : ""
            }`}
        >
            {empilharDuasLinhas ? (
                <span className="analysis-sort-text">
                    <span>{partes[0]}</span>
                    <span>{partes[1]}</span>
                </span>
            ) : (
                <span>{titulo}</span>
            )}
            <span className="analysis-sort-icon">{getSortIcon(key)}</span>
        </span>
    );
};

const ResultTable = ({
    currentItems,
    handleSort,
    getSortIcon,
    onView,
    onOpenHistory,
    historyLoading = false,
    formatDate,
}: ResultTableProps) => {
    return (
        <div className="analysis-table-scroll">
            <Table bordered hover className="analysis-table">
                <thead>
                    <tr>
                        <th
                            onClick={() => handleSort("solicitacao")}
                            className="sortable-header"
                        >
                            {renderSortableTitle("Solicitação", "solicitacao", getSortIcon)}
                        </th>

                        <th
                            onClick={() => handleSort("situacao")}
                            className="sortable-header"
                        >
                            {renderSortableTitle("Situação", "situacao", getSortIcon)}
                        </th>

                        <th
                            onClick={() => handleSort("envioDocumentacao")}
                            className="sortable-header"
                        >
                            {renderSortableTitle(
                                "Envio Documentação",
                                "envioDocumentacao",
                                getSortIcon,
                            )}
                        </th>

                        <th
                            onClick={() => handleSort("ultimaAtualizacao")}
                            className="sortable-header"
                        >
                            {renderSortableTitle(
                                "Última Atualização",
                                "ultimaAtualizacao",
                                getSortIcon,
                            )}
                        </th>

                        <th
                            onClick={() => handleSort("solicitacaoAdequacao")}
                            className="sortable-header"
                        >
                            {renderSortableTitle(
                                "Solicitação Adequação",
                                "solicitacaoAdequacao",
                                getSortIcon,
                            )}
                        </th>

                        <th
                            onClick={() => handleSort("adequacaoDocumentacao")}
                            className="sortable-header"
                        >
                            {renderSortableTitle(
                                "Adequação Documentação",
                                "adequacaoDocumentacao",
                                getSortIcon,
                            )}
                        </th>

                        <th
                            onClick={() => handleSort("conclusaoAnalise")}
                            className="sortable-header"
                        >
                            {renderSortableTitle(
                                "Conclusão Análise",
                                "conclusaoAnalise",
                                getSortIcon,
                            )}
                        </th>

                        <th className="text-center">Ações</th>
                    </tr>
                </thead>

                <tbody>
                    {currentItems.length > 0 ? (
                        currentItems.map((item, index) => {
                            const { icon, className } = getSituationConfig(item.situacao);
                            const labelAcaoPrincipal =
                                getLabelAcaoPrincipalPorLinha(item);
                            const modoVisualizar = labelAcaoPrincipal === "Visualizar";

                            return (
                                <tr key={index}>
                                    <td>{item.solicitacao}</td>

                                    <td>
                                        <div className="situation-cell centered-cell">
                                            <span className={`situation-icon ${className}`}>
                                                {icon}
                                            </span>
                                            <span className="situation-text">{item.situacao}</span>
                                        </div>
                                    </td>

                                    <td>{formatDate(item.envioDocumentacao)}</td>
                                    <td>{formatDate(item.ultimaAtualizacao)}</td>
                                    <td>{formatDate(item.solicitacaoAdequacao)}</td>
                                    <td>{formatDate(item.adequacaoDocumentacao)}</td>
                                    <td>{formatDate(item.conclusaoAnalise)}</td>

                                    <td>
                                        <div className="action-buttons d-flex gap-2 justify-content-center">
                                            <OverlayTrigger
                                                placement="top"
                                                overlay={
                                                    <Tooltip id={`view-${item.id}`}>
                                                        {labelAcaoPrincipal}
                                                    </Tooltip>
                                                }
                                            >
                                                <Button
                                                    className="btn-edit"
                                                    size="sm"
                                                    onClick={() => onView(item)}
                                                    aria-label={labelAcaoPrincipal}
                                                >
                                                    {modoVisualizar ? (
                                                        <FaEye aria-hidden />
                                                    ) : (
                                                        <FaEdit aria-hidden />
                                                    )}
                                                </Button>
                                            </OverlayTrigger>

                                            <OverlayTrigger
                                                placement="top"
                                                overlay={
                                                    <Tooltip id={`history-${item.id}`}>
                                                        {historyLoading
                                                            ? "Carregando histórico..."
                                                            : "Ver histórico"}
                                                    </Tooltip>
                                                }
                                            >
                                                <Button
                                                    variant="secondary"
                                                    size="sm"
                                                    disabled={historyLoading}
                                                    aria-busy={historyLoading}
                                                    onClick={() => onOpenHistory(item)}
                                                >
                                                    <FaClock />
                                                </Button>
                                            </OverlayTrigger>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })
                    ) : (
                        <tr>
                            <td colSpan={8} className="empty-row">
                                Nenhuma solicitação encontrada.
                            </td>
                        </tr>
                    )}
                </tbody>
            </Table>
        </div>
    );
};

export default ResultTable;